import {Injectable} from '@angular/core';
import {AuthService} from '@webmate-web-core/shared/services/auth.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import {CustomerId, DeviceSlotId, DeviceSlotTemplateId, MachineId, MachinePoolId} from '@webmate-web-core/shared/types/ids';
import {Observable} from 'rxjs';
import {Machine, machineFromBackendObject, MachinePool, machinePoolFromBackendObject} from '@webmate-web-core/shared/types/devices/machine';
import {map} from 'rxjs/operators';
import {DeviceSlot, deviceSlotFromBackendObject} from '@webmate-web-core/shared/types/devices/device-slot';
import {CustomerInfo} from '@webmate-web-core/shared/types/customer';
import {UserInfo} from '@webmate-web-core/shared/types/user/user-info';
import {GenericApiResponse} from '@webmate-web-core/shared/api/apibase.api';
import {ProjectInfo} from '@webmate-web-core/shared/types/projectInfo';
import {DeviceSlotTemplate, slotTemplateFromBackendObject} from '@webmate-web-core/shared/types/devices/device-template';
import {FeatureMap} from '@webmate-web-core/shared/types/feature';
import {User} from '@webmate-web-core/shared/types/user/user';
import {DeviceSlotHistoryEvent} from "@webmate-web-core/shared/types/interfaces/device-slot-history-event.interface";
import {Device} from "@webmate-web-core/shared/types/devices/device";
import {RefeedAuditsDTO, RefeedFindingsDTO, RefeedTestRunsDTO} from '@webmate-web-core/shared/types/refeed';

@Injectable({
    providedIn: "root"
})
export class WmAdminApi {
    constructor(public OAuth2AuthService: AuthService, private http: HttpClient) {}

    searchUser(searchstring: string): Observable<User[]> {
        return this.http.get<any>("/wmadmin/users/search?q=" + searchstring);
    }

    createCustomer(createCustomerDTO: any): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>("/wmadmin/customers", createCustomerDTO);
    }

    deleteCustomer(customerId: CustomerId): Observable<GenericApiResponse> {
        return this.http.delete<GenericApiResponse>("/wmadmin/customers/" + customerId);
    }

    getBillingInfoForCustomer(customerId: CustomerId, slotTemplateId: DeviceSlotTemplateId): Observable<any> {
        return this.http.get(`/wmadmin/customers/${customerId}/slotTemplate/${slotTemplateId}/billingInfos`);
    }

    getMachinesForCustomer(customerId: CustomerId, poolId: MachinePoolId): Observable<Machine[]> {
        let querryParam = new HttpParams();
        if (!!poolId) {
            querryParam = querryParam.append("poolId", poolId);
        }

        return this.http.get<any[]>("/wmadmin/customers/" + customerId + "/machines", {params: querryParam})
            .pipe(map(x => x.map(machine => machineFromBackendObject(machine))));
    }

    getPool(poolId: MachinePoolId): Observable<MachinePool> {

        return this.http.get<any[]>("/wmadmin/pools/" + poolId, )
            .pipe(map(pool => machinePoolFromBackendObject(pool)));
    }

    getAssignedMachinesForCustomer(cutomerId: CustomerId): Observable<MachineId[]> {
        return this.http.get<MachineId[]>("/wmadmin/customers/" + cutomerId + "/assignedMachines");
    }

    getFeaturesForCustomer(customerId: CustomerId): Observable<FeatureMap> {
        return this.http.get<FeatureMap>("/wmadmin/customers/" + customerId + "/features");
    }

    getSlotsForCustomer(customerId: CustomerId): Observable<DeviceSlot[]> {
        return this.http.get<Array<any>>("/wmadmin/customers/" + customerId + "/slots").pipe(map( slotArr => slotArr.map(s => deviceSlotFromBackendObject(s))))
    }

    deleteSlot(slotId: DeviceSlotId): Observable<any> {
        return this.http.delete('/wmadmin/slots/' + slotId);
    }

    createSlot(customerId: CustomerId, slotConfig: any): Observable<any> {
        return this.http.post('/wmadmin/customers/' + customerId + '/slots', slotConfig);
    }

    updateSlot(customerId: CustomerId, slot: DeviceSlot): Observable<any> {
        return this.http.post('/wmadmin/slots/' + slot.id, {...slot, customerId});
    }

    searchCustomer(searchstring: string): Observable<CustomerInfo[]> {
        return this.http.get<any>("/wmadmin/customers/search?name=" + searchstring).pipe(map(customerList => {
            return customerList.map((customer: any) => {
                customer.name = customer.properties.name;
                return customer;
            })
        }));
    }

    getBillForCustomerAtTime(customerId: CustomerId, since: string, until?: string): Observable<Blob> {
        // /wmadmin/bill/<customerId>?since=<datetime>&until=<datetime> in the format of 2022-11-08 07:49:04.223565 +00:00. until is optional
        let querryParam = new HttpParams().append('since', since);
        if (!!until) {
            querryParam = querryParam.append("until", until);
        }
        return this.http.get('/wmadmin/bill/' + customerId, {params: querryParam, responseType: 'blob'});
    }

    getProjectsForCustomer(customerId: CustomerId): Observable<ProjectInfo[]> {
        return this.http.get<ProjectInfo[]>("/wmadmin/customers/" + customerId + "/projects");
    }

    getCustomer(customerId: CustomerId): Observable<CustomerInfo> {
        return this.http.get<any>('/wmadmin/customers/' + customerId).pipe(map(customer => {
            // The following line only works if the route returns a non-null CustomerDTO; this should always be the case.
            customer.name = customer.properties.name;
            return customer;
        }));
    }

    getSlotTemplatessForCustomer(customerId: CustomerId): Observable<DeviceSlotTemplate[]> {
        return this.http.get<any[]>('/wmadmin/slotTemplates', {params: {customerId: customerId}}).pipe(map((response: any[]) => {
            return response.map(t => slotTemplateFromBackendObject(t));
        }));
    }

    getSlotTemplate(slotTemplateId: DeviceSlotTemplateId): Observable<DeviceSlotTemplate> {
        return this.http.get<any>('/wmadmin/slotTemplates/' + slotTemplateId).pipe(map((response: any) => {
            return slotTemplateFromBackendObject(response);
        }));
    }

    getDeviceSlotHistory(slotId?: string, searchQuery?: string, startDate?: string, endDate?: string, historySearch?: boolean): Observable<DeviceSlotHistoryEvent[]> {
        let params = new HttpParams();
        if (!!slotId) {
            params = params.append('slot', slotId);
        }
        if (!!searchQuery || !slotId) {
            // We need to pass an empty string explicitly --
            // otherwise, we will get more than only the newest event per slot.
            params = params.append('search', searchQuery ?? '');
        }
        if (!!startDate) {
            params = params.append('since', startDate);
        }
        if (!!endDate) {
            params = params.append('until', endDate);
        }
        if (!!historySearch) {
            params = params.append('deep', historySearch);
        }
        return this.http.get<DeviceSlotHistoryEvent[]>('/wmadmin/device-slot-history', {params: params});
    }

    getAllDeployedDevices(): Observable<Device[]> {
        return this.http.get<Device[]>('/wmadmin/devices/deployed');
    }

    clearSlotUpdatePending(slotId: DeviceSlotId): Observable<any> {
        return this.http.post('/wmadmin/slots/' + slotId + '/clear-update-pending', {});
    }

    refeedFindings(refeedDto: RefeedFindingsDTO): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>('/testmgmt/refeed-findings', refeedDto);
    }

    refeedAudits(refeedDto: RefeedAuditsDTO): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>('/audit/refeed-audits', refeedDto);
    }

    refeedTestRuns(refeedDto: RefeedTestRunsDTO): Observable<GenericApiResponse> {
        return this.http.post<GenericApiResponse>('/testmgmt/refeed', refeedDto);
    }
}
